<template>
  <div>
    <div v-if="loadingAnalytics" class="loading">
      <v-progress-circular color="primary" indeterminate size="50" />
    </div>
    <div v-else>
      <v-row align="center">
        <v-col cols="12" md="5">
          <p class="text-h4 font-weight-medium">{{ user.full_name }}</p>
          <p class="text--secondary">{{ user.email }}</p>
        </v-col>
        <v-col cols="12" xl="7">
          <v-row dense>
            <v-col
              v-for="(item, index) in visibleAnalytics"
              :key="index"
              cols="12"
              lg="3"
              sm="6"
            >
              <analytics-card
                :body="item.body"
                :color="item.color"
                :loading="loadingAnalytics"
                :title="item.title"
              />
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <v-row class="mt-10">
        <v-col cols="0" lg="7" md="4" />
        <v-col cols="12" lg="3" md="5">
          <base-autocomplete
            v-model="assignment"
            clearable
            item-text="text"
            item-value="uuid"
            :items="assignmentsChoices"
            :loading="loadingAssignments"
            outlined
            placeholder="Search Assignments"
          />
        </v-col>
        <v-col cols="12" lg="2" md="3">
          <base-select
            v-model="status"
            clearable
            :items="statusChoices"
            outlined
            placeholder="Status"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <base-data-table
            class="mb-10"
            :headers="headers"
            :items="items"
            :items-per-page="params.itemsPerPage"
            :loading="loading"
            :page="params.page"
          >
            <template v-slot:[`header.action`]>
              <total-entries-count
                :items-per-page="params.itemsPerPage"
                :page="params.page"
                :total="total"
              />
            </template>

            <template v-slot:[`item.evaluation_status`]="{ item }">
              <evaluation-status-display :status="item.evaluation_status" />
            </template>

            <template v-slot:[`item.assignment.title`]="{ item }">
              <p>
                {{
                  item.assignment.title
                    | formatAssignmentTitle(item.assignment.short_title)
                }}
              </p>
            </template>

            <template v-slot:[`item.action`]="{ item }">
              <v-btn
                color="primary"
                link
                small
                text
                :to="{
                  name: 'ViewSubmission',
                  params: {
                    submissionId:
                      item.evaluation_status ===
                      EVALUATION_STATUS.UNDER_EVALUATION
                        ? item.latest_submission
                        : item.evaluated_submission,
                  },
                }"
              >
                View Submission
              </v-btn>
            </template>
            <template v-slot:footer>
              <base-paginator
                :items-per-page="params.itemsPerPage"
                :page="params.page"
                :total="total"
                @change="params.page = $event"
              />
            </template>
          </base-data-table>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import AnalyticsCard from '@/components/_shared/AnalyticsCard'
import TotalEntriesCount from '@/components/_shared/TotalEntriesCount'
import EvaluationStatusDisplay from '@/components/EvaluationStatusDisplay'
import { EVALUATION_STATUS } from '@/utils/constants'
import filters from '@/utils/mixins/filters'

export default {
  name: 'ProfessorAssignments',

  components: {
    AnalyticsCard,
    TotalEntriesCount,
    EvaluationStatusDisplay,
  },

  mixins: [filters],

  data: () => ({
    EVALUATION_STATUS,

    assignment: null,
    loadingAssignments: false,
    assignmentsChoices: [],

    /**
     * Evaluation status
     */
    status: null,
    statusChoices: [
      { text: 'Pending', value: EVALUATION_STATUS.UNDER_EVALUATION },
      { text: 'Challenged', value: EVALUATION_STATUS.CHALLENGED },
      {
        text: "Awaiting Professor's Response",
        value: EVALUATION_STATUS.CHANGES_REQUESTED,
      },
      {
        text: 'Quality Assurance Complete',
        value: EVALUATION_STATUS.COMPLETED,
      },
    ],

    user: {
      full_name: 'Beauregard Broderick',
      email: 'anshsaini@gmail.com',
    },

    params: {
      page: 1,
      itemsPerPage: 10,
      search: '',
    },

    headers: [
      {
        text: "User's Name",
        sortable: false,
        value: 'user.full_name',
      },
      {
        text: 'Assignment Name',
        sortable: false,
        value: 'assignment.title',
      },
      {
        text: 'Status',
        sortable: false,
        value: 'evaluation_status',
      },
      {
        text: '',
        sortable: false,
        value: 'action',
        align: 'end',
      },
    ],

    analytics: [],
    loadingAnalytics: false,

    loading: false,
    total: 0,
    items: [],
  }),

  computed: {
    professorId() {
      return this.$route.params.professorId
    },

    visibleAnalytics() {
      if (this.analytics.length === 0) {
        const dummyObject = {
          body: 'random',
          color: 'purple',
          title: 'Users Allocated',
        }
        // Need 4 dummy loading cards
        return new Array(4).fill(dummyObject)
      }

      return this.analytics
    },
  },

  watch: {
    status() {
      this.getList()
    },
    assignment() {
      this.getList()
    },
    'params.page'() {
      this.getList()
    },
  },

  mounted() {
    this.getList()
    this.overview()
    this.getAssignmentsFilter()
  },

  methods: {
    async overview() {
      this.loadingAnalytics = true

      try {
        const res = await this.$api.assignments.iqaProfessorOverview({
          professorId: this.professorId,
        })
        const analytics = res.data.data
        this.user = analytics.user
        this.analytics = [
          {
            body: `${analytics.accepted_evaluations}:${analytics.changed_evaluations}`,
            color: 'green',
            title: 'Accepted:Changed',
          },
          {
            body: `${analytics.awaiting_response}/${analytics.total_evaluations}`,
            color: 'orange',
            title: 'Awaiting Response',
          },
          {
            body: `${analytics.pending_evaluations}/${analytics.total_evaluations}`,
            color: 'yellow',
            title: 'Pending Evaluations',
          },
          {
            body: `${analytics.challenged_evaluations}/${analytics.total_evaluations}`,
            color: 'red',
            title: 'Challenged Evaluations',
          },
        ]
        this.loadingAnalytics = false
      } catch (err) {
        console.error(err)
      }
    },

    async getList() {
      this.loading = true

      const { page, search } = this.params

      const data = {
        params: {
          professor: this.professorId,
          page,
          search,
          fields: ['evaluation_status'],
        },
      }
      if (this.status !== null) {
        data.params.evaluation_status = this.status
      }
      if (this.assignment !== null) {
        data.params.assignment = this.assignment
      }
      try {
        const res = await this.$api.assignments.listAssignments(data)
        this.items = res.data.data
        this.total = res.data.total
      } catch (err) {
        console.error(err)
      } finally {
        this.loading = false
      }
    },

    async getAssignmentsFilter() {
      this.loadingAssignments = true
      try {
        const res = await this.$api.assignments.getAssignments({
          params: { page: 'all' },
        })
        this.assignmentsChoices = res.data.data.map(assignment => ({
          ...assignment,
          text: filters.filters.formatAssignmentTitle(
            assignment.title,
            assignment.short_title
          ),
        }))
      } catch (err) {
        console.error(err)
      } finally {
        this.loadingAssignments = false
      }
    },
  },
}
</script>

<style scoped>
.loading {
  height: 70vh;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
